import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { MenubarModule } from 'primeng/menubar';
import { PopoverModule } from 'primeng/popover';
import { DividerModule } from 'primeng/divider';
import { merge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import { AuthService } from 'src/app/auth.service';
import { SystemService } from 'src/app/system.service';
import { NotificationsService } from 'src/app/notifications.service';
import { MessagingService } from 'src/app/messaging.service';
import { FcmTokenService } from 'src/app/fcm-token.service';
import { YellowfinService } from 'src/app/yellowfin.service';
import { ContactsService } from 'src/app/contacts.service';
import { UnitService } from 'src/app/unit.service';
import { NotificationTitle } from '../../../../../../models/notifications.model';
import { sortBy } from '../../../../../../utils/sortby';

import { NavMenuService } from './nav-menu.service';
import { KeycloakService } from 'src/app/keycloak.service';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ContactReference } from 'models/contact-reference.models';
import { Project } from 'models/project.models';
import { ContactReferenceService } from '../../../contact-reference.service';
import { UserService } from '../../../user.service';
import { AppPasswordResetDialogComponent } from '../../../shared/components/app-password-reset-dialog/app-password-reset-dialog.component';
import { FreshdeskWebchat } from 'src/app/freshdesk-webchat.service';
import { environment } from 'src/environments/environment';

@Component( {
    selector: 'app-nav-bar',
    templateUrl: './nav-bar.component.html',
    styleUrls: ['./nav-bar.component.scss'],
    imports: [CommonModule, FormsModule, MenubarModule, PopoverModule, DividerModule, SelectModule, AutoCompleteModule, ButtonModule, RouterModule, AppPasswordResetDialogComponent, BadgeModule, AvatarModule, MenuModule],
} )

export class NavBarComponent implements OnInit, OnDestroy {
	userService = inject( UserService );
	contactRefService = inject( ContactReferenceService );
	private authService = inject( AuthService );
	private router = inject( Router );
	private notificationsService = inject( NotificationsService );
	private messagingService = inject( MessagingService );
	private fcmTokenService = inject( FcmTokenService );
	private unitService = inject( UnitService );
	private yfService = inject( YellowfinService );
	private contactService = inject( ContactsService );
	private keycloakService = inject( KeycloakService );	
	private navMenuService = inject( NavMenuService );
	private ss = inject( SystemService );
	private freshdeskWebchatService = inject( FreshdeskWebchat );

	private destroy$ = new Subject<void>();

	readonly notificationTitle = NotificationTitle;

	readonly state$ = this.navMenuService.state$;
	readonly logoUrl$ = this.navMenuService.logoUrl$;

	searchPanelOpen = false;
	readonly siaEnabled = environment.siaFreshdeskAIAgentEnabled;
	recentNotifications: any[] = [];
	oldNotifications: any[] = [];
	filteredGroups: any[];
	buildings: any[];
	communities: Project[];
	toSearch: string;
	viewContacts = true;
	viewUnits = true;
	showNavbarSearch = true;
	showPasswordResetDialog = false;
	passwordPolicyUsername = '';
	private searchSeq = 0;
	private readonly searchInput$ = new Subject<string>();
	private searchIndex: { sources: unknown[]; contacts: SearchEntry[]; units: SearchEntry[] } | null = null;
	private static readonly MAX_RESULTS_PER_GROUP = 25;

	viewChartOfAccountsAccessRights: ContactReference[] = [];

	private isGlobalViewUrl ( url: string | null | undefined ): boolean {
		const normalizedUrl = ( url ?? '' ).split( '?' )[ 0 ];
		return normalizedUrl === '/global-view' || normalizedUrl.startsWith( '/global-view/' );
	}

	async ngOnInit (): Promise<void> {
		this.searchInput$.pipe(
			debounceTime( 250 ),
			distinctUntilChanged(),
			takeUntil( this.destroy$ ),
		).subscribe( query => void this.runSearch( query ) );

		// Init reactive state (resolves permissions, subscribes to url+community streams)
		await this.navMenuService.init();

		// Notifications count from Firebase — pushed into NavMenuService so state$ re-emits
		this.messagingService.notificationsCount.pipe(
			takeUntil( this.destroy$ ),
		).subscribe( count => this.navMenuService.setNotificationsCount( count ) );

		// Sync password reset dialog visibility
		merge(
			this.authService.currentUserData.pipe(
				take( 1 ),
				filter( () => this.isGlobalViewUrl( this.router.url ) ),
			),
			this.router.events.pipe(
				filter( ( e ): e is NavigationEnd => e instanceof NavigationEnd ),
				filter( e => this.isGlobalViewUrl( e.urlAfterRedirects ?? e.url ) ),
			),
		).pipe(
			takeUntil( this.destroy$ ),
		).subscribe( async () => await this.syncPasswordResetDialogVisibility() );

		// Firebase push permission + token registration
		this.messagingService.requestPermission().pipe(
			takeUntil( this.destroy$ ),
		).subscribe( {
			next: async token => {
				const hasToken = await this.authService.hasFCMToken();
				if ( !hasToken && token !== null ) {
					this.authService.setFCMToken( token );
					this.fcmTokenService.register( token )
						.then( () => this.messagingService.receiveMessage() )
						.catch( err => console.error( err ) );
				} else {
					this.messagingService.receiveMessage();
				}
			},
			error: err => console.error( 'Unable to get notification permission.', err ),
		} );
	}

	private async syncPasswordResetDialogVisibility (): Promise<void> {
		const email = this.getCurrentUserEmail();
		this.passwordPolicyUsername = email && email.includes( '@' ) ? email.split( '@' )[ 0 ] : '';

		const migrationStatus = await this.keycloakService.getMigrationStatus();
		if( migrationStatus ) {
			this.showPasswordResetDialog = migrationStatus.showMigrationPopup;
		}
	}

	private getCurrentUserEmail (): string {
		const currentUserEmail = this.ss?.currentUser?.email ?? this.authService.currentUserData.getValue()?.email;
		return currentUserEmail ?? '';
	}

	public async onPasswordResetDialogConfirm ( newPassword: string ): Promise<void> {
		const response = await this.keycloakService.updatePasswordAndMigrateToKeycloak( { newPassword } )
		if ( response?.success ) {
			this.showPasswordResetDialog = false;
			await this.onLogout();
		}
	}

	// ── Community dropdown ────────────────────────────────────────────────────

	changeValue ( event: any ): void {
		const item = event?.value;
		const list = this.navMenuService.communityListSnapshot;
		if ( list.length > 0 && item === list[0] ) {
			this.router.navigate( ['/global-view'] );
		} else if ( item ) {
			this.router.navigate( [`/projects/${( item as any ).link ?? item.id}`] );
		}
	}

	// ── Profile dropdown ──────────────────────────────────────────────────────

	// No profile image available yet, so the avatar shows initials instead:
	// first+last name initial ("Ahmad Munam" -> "AM"), single-word name ->
	// its first char, no name at all -> the email's first char.
	getUserInitials ( displayName: string | null | undefined, email: string | null | undefined ): string {
		const words = ( displayName || '' ).trim().split( /\s+/ ).filter( Boolean );
		if ( words.length > 1 ) {
			return ( words[ 0 ][ 0 ] + words[ words.length - 1 ][ 0 ] ).toUpperCase();
		}
		if ( words.length === 1 ) {
			return words[ 0 ][ 0 ].toUpperCase();
		}
		const trimmedEmail = ( email || '' ).trim();
		return trimmedEmail ? trimmedEmail[ 0 ].toUpperCase() : '';
	}

	profileChangeValue ( event: any ): void {
		const label = event?.value?.label;
		switch ( label ) {
			case 'Profile':
				this.router.navigate( ['/profile'] );
				break;
			case 'Contact Us':
				this.router.navigate( ['/contact-us'] );
				break;
			case 'Sign Out':
				this.onLogout().catch( err => console.error( 'Logout failed:', err ) );
				break;
		}
	}

	// Drives aria-expanded on the profile trigger
	profileMenuOpen = false;

	// Cached per items array, so change detection keeps handing p-menu the same model
	private profileMenuModels = new WeakMap<MenuItem[], MenuItem[]>();

	profileMenuModel ( items: MenuItem[] | null | undefined ): MenuItem[] {
		if ( !items ) return [];
		let model = this.profileMenuModels.get( items );
		if ( !model ) {
			// No routerLink: profileChangeValue handles every action, as it did for the old dropdown
			model = items.map( item => ( { label: item.label, icon: item.icon, command: () => this.profileChangeValue( { value: item } ) } ) );
			this.profileMenuModels.set( items, model );
		}
		return model;
	}

	// ── Restricted header ─────────────────────────────────────────────────────

	goToRequests (): void {
		this.router.navigate( ['/request-admin'] );
	}

	goToVisitors (): void {
		this.router.navigate( ['/visitors'] );
	}

	// ── Admin navigation ──────────────────────────────────────────────────────

	goToAdminPage (): void {
		this.router.navigate( ['administration'] );
	}

	goToGlobalAdminPage (): void {
		this.router.navigate( ['globalAdministration'] );
	}

	// ── Sia ───────────────────────────────────────────────────────────────────

	toggleSia (): void {
		this.freshdeskWebchatService.toggle();
	}

	// ── Notifications ─────────────────────────────────────────────────────────

	async getNotifications (): Promise<void> {
		const all = ( await this.notificationsService.getNotifications() ).sort( sortBy( 'notifyTime', true ) );
		this.recentNotifications = all.filter( n => n.status === 'new' );
		this.oldNotifications = all.filter( n => n.status !== 'new' );
	}

	// ── Global search ─────────────────────────────────────────────────────────

	onSearchQueryChange ( query: string ): void {
		this.searchPanelOpen = true;
		if ( !( query || '' ).trim() ) {
			this.searchSeq++;
			this.filteredGroups = [];
		}
		this.searchInput$.next( query || '' );
	}

	private async runSearch ( rawQuery: string ): Promise<void> {
		const query = rawQuery.toLowerCase().trim();
		const seq = ++this.searchSeq;

		if ( query === '' ) {
			// Empty groups left in here would still render as a blank panel.
			this.filteredGroups = [];
			return;
		}

		const index = await this.getSearchIndex();
		if ( seq !== this.searchSeq ) return;

		// Drop empty groups — otherwise the panel still renders with no visible
		// rows, i.e. a blank rectangle.
		this.filteredGroups = [
			{ label: 'Contacts', value: 'contacts', image: '/assets/icons/administration/contacts.svg', items: this.matchTop( index.contacts, query ) },
			{ label: 'Units', value: 'units', image: '/assets/icons/community/units.svg', items: this.matchTop( index.units, query ) },
		].filter( group => group.items.length > 0 );
	}

	private matchTop ( entries: SearchEntry[], query: string ): any[] {
		const matches: any[] = [];
		for ( const entry of entries ) {
			if ( entry.haystack.includes( query ) ) {
				matches.push( entry.item );
				if ( matches.length >= NavBarComponent.MAX_RESULTS_PER_GROUP ) break;
			}
		}
		return matches;
	}

	private async getSearchIndex (): Promise<{ sources: unknown[]; contacts: SearchEntry[]; units: SearchEntry[] }> {
		const [contacts, unitMap] = await Promise.all( [
			this.contactService.getcontactArray(),
			this.unitService.getAllUnits(),
		] );
		const buildings = this.navMenuService.buildings;
		const communities = this.navMenuService.communities;
		const sources: unknown[] = [contacts, unitMap, buildings, communities];

		if ( this.searchIndex && this.searchIndex.sources.every( ( src, i ) => src === sources[ i ] ) ) {
			return this.searchIndex;
		}

		this.searchIndex = {
			sources,
			contacts: this.buildContactIndex( contacts || [] ),
			units: this.buildUnitIndex( Object.values( unitMap || {} ), buildings, communities ),
		};
		return this.searchIndex;
	}

	private buildContactIndex ( contacts: any[] ): SearchEntry[] {
		const seen = new Set<string>();
		const entries: SearchEntry[] = [];
		for ( const contact of contacts ) {
			const key = contact.id ? `id:${contact.id}` : `n:${( contact.displayName || '' ).toLowerCase()}`;
			if ( seen.has( key ) ) continue;
			seen.add( key );
			const parts = [contact.displayName || ''];
			for ( const email of contact.emails || [] ) if ( email?.email ) parts.push( email.email );
			for ( const phone of contact.phones || [] ) if ( phone?.number != null ) parts.push( phone.number.toString() );
			entries.push( {
				haystack: parts.join( '\u0000' ).toLowerCase(),
				item: { label: contact.displayName, value: contact, type: 'contact' },
			} );
		}
		return entries;
	}

	private buildUnitIndex ( units: any[], buildings: any[], communities: Project[] ): SearchEntry[] {
		const buildingById = new Map<string, any>( ( buildings || [] ).map( b => [b.id, b] as [string, any] ) );
		const communityById = new Map<string, any>( ( communities || [] ).map( c => [( c as any ).id, c] as [string, any] ) );

		const seen = new Set<string>();
		const entries: SearchEntry[] = [];
		for ( const unit of units ) {
			if ( !unit.building ) continue;
			const key = unit.id ? `id:${unit.id}` : `l:${unit.name}`;
			if ( seen.has( key ) ) continue;
			seen.add( key );
			const building = buildingById.get( unit.building );
			const community = communityById.get( building?.community );
			const labelCommunity = communityById.get( building?.community || unit.project );
			entries.push( {
				haystack: `${unit.name}/${building?.name}/${community?.name}`.toLowerCase(),
				item: {
					label: [unit.name, building?.name, labelCommunity?.name].filter( Boolean ).join( '/' ),
					value: unit,
					type: 'unit',
				},
			} );
		}
		return entries;
	}

	trackSearchGroup ( _index: number, group: any ): string {
		return group.value;
	}

	trackSearchResult ( _index: number, item: any ): string {
		return `${item.type}:${item.value?.id ?? item.label}`;
	}

	searchResult ( event: any ): void {
		this.toSearch = event.label;
		this.router.navigate( [`global-view/search/${event.type}/${event.value.id}`] );
	}

	closeSearchPanel (): void {
		setTimeout( () => {
			this.searchPanelOpen = false;
		}, 150 );
	}

	selectSearchResult ( item: any ): void {
		this.searchPanelOpen = false;
		this.searchResult( item );
	}

	// ── Logout ────────────────────────────────────────────────────────────────

	private async onLogout (): Promise<void> {
		try {
			const sso = await firstValueFrom( this.yfService.currentSSOToken$ );
			if ( sso ) {
				await firstValueFrom( this.yfService.destroySSOToken( ( sso as any )._links.logout.href ) );
			}
			const yf = ( window as any ).yellowfin;
			if ( yf ) {
				const login = await firstValueFrom( this.yfService.loginToken$ );
				await firstValueFrom( this.yfService.destroyLoginToken( ( login as any )._links.self.href ) );
				await yf.logoff();
			}
		} catch ( err ) {
			console.error( 'Yellowfin teardown failed:', err );
		} finally {
			this.navMenuService.reset();
			await this.ss.signout();
		}
	}

	ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}

interface SearchEntry {
	haystack: string;
	item: any;
}
